import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { planBadgeClass, planLabel, useAuth } from "../lib/auth";
import { useTenant } from "../lib/tenant";
import type { AuthUser } from "../lib/api";

/**
 * Sticky top nav. Kept deliberately compact:
 *   • Signed OUT — only the two things an anonymous visitor can actually use
 *     (Dashboard, New Crawl) plus the Sign-in button. No wall of links that
 *     bounce to a sign-in prompt.
 *   • Signed IN — Dashboard, New Crawl, Runs stay as top-level links; the rest
 *     collapse into two grouped dropdowns (Automate, Insights) so the bar
 *     doesn't sprawl.
 *   • Crawl Map / Test Generator are crawl-scoped, so they only appear as a
 *     context cluster while a crawl is open (no perpetually-disabled links).
 *
 * The right edge hosts the auth controls: an avatar dropdown when signed in,
 * or a "Sign in" button when signed out. Both are hidden when the API has no
 * auth configured (`useAuth().disabled === true`).
 */
export function TopNav(): JSX.Element {
  const { id } = useParams();
  const mapHref = id ? `/crawls/${id}` : "/";
  const genHref = id ? `/crawls/${id}/tests` : "/";
  const { user, disabled: authDisabled, ready } = useAuth();
  const tenant = useTenant();
  const isSingleTenant = tenant.mode === "single-tenant";

  return (
    <header className="sticky top-0 z-40 border-b border-slate-800 bg-slate-950/90 backdrop-blur">
      <div className="mx-auto max-w-7xl px-6 py-3 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2">
          {isSingleTenant && tenant.logoUrl ? (
            <img
              src={tenant.logoUrl}
              alt=""
              className="h-8 w-8 rounded-lg object-cover"
            />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-500 grid place-items-center text-white font-bold">
              U
            </div>
          )}
          <div>
            <div className="font-semibold leading-tight">UAIP</div>
            <div className="text-xs text-slate-400 leading-tight">
              UI Automation Intelligence · v0.1
            </div>
          </div>
        </Link>
        {isSingleTenant && (
          <span
            className="rounded-full border border-indigo-700/60 bg-indigo-950/60 px-2 py-0.5 text-xs font-medium text-indigo-200"
            title={`Single-tenant deployment · tenant id: ${tenant.tenantId}`}
          >
            {tenant.tenantName}
          </span>
        )}

        <nav className="ml-auto flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
          <TopLink to="/" end>
            Dashboard
          </TopLink>
          <TopLink to="/crawls/new">New Crawl</TopLink>

          {/* Authenticated work views — hidden until signed in. */}
          {user && (
            <>
              <TopLink to="/runs">Runs</TopLink>
              <NavDropdown label="Automate" paths={["/schedules", "/notifications"]}>
                <DropItem to="/schedules">Schedules</DropItem>
                <DropItem to="/notifications">Notifications</DropItem>
              </NavDropdown>
              <NavDropdown
                label="Insights"
                paths={["/traces", "/coverage", "/heals", "/audit-log"]}
              >
                <DropItem to="/traces">Trace</DropItem>
                <DropItem to="/coverage">Coverage</DropItem>
                <DropItem to="/heals">Self-Heal</DropItem>
                <DropItem to="/audit-log">Audit log</DropItem>
              </NavDropdown>
            </>
          )}

          {/* Crawl-scoped context — only while a crawl is open. */}
          {id && (
            <div className="flex items-center gap-x-5 border-l border-slate-800 pl-5">
              <TopLink to={mapHref} end>
                Crawl Map
              </TopLink>
              <TopLink to={genHref}>Test Generator</TopLink>
            </div>
          )}
        </nav>

        {/* Auth cluster: hidden entirely if the API doesn't speak auth. */}
        {!authDisabled && (
          <div className="flex items-center">
            {!ready ? (
              <div className="h-8 w-8 rounded-full border border-slate-800 bg-slate-900" />
            ) : user ? (
              <UserMenu user={user} />
            ) : (
              <SignInCta />
            )}
          </div>
        )}
      </div>
    </header>
  );
}

function TopLink(props: {
  to: string;
  end?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <NavLink
      to={props.to}
      end={props.end ?? false}
      className={({ isActive }) =>
        `hover:text-white ${isActive ? "text-white" : "text-slate-300"}`
      }
    >
      {props.children}
    </NavLink>
  );
}

/**
 * A labelled nav dropdown (Automate / Insights). Click to open; closes on
 * outside-click, Escape, or navigation. The label lights up when the current
 * route is one of `paths`, so the active section is always visible even while
 * collapsed.
 */
function NavDropdown(props: {
  label: string;
  paths: string[];
  children: React.ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const sectionActive = props.paths.some((p) => location.pathname.startsWith(p));

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 hover:text-white ${
          sectionActive || open ? "text-white" : "text-slate-300"
        }`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {props.label}
        <svg
          className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          onClick={() => setOpen(false)}
          className="absolute left-0 top-full z-50 mt-2 w-48 rounded-lg border border-slate-800 bg-slate-950 p-1.5 shadow-xl"
        >
          {props.children}
        </div>
      )}
    </div>
  );
}

function DropItem(props: { to: string; children: React.ReactNode }): JSX.Element {
  return (
    <NavLink
      to={props.to}
      role="menuitem"
      className={({ isActive }) =>
        `block rounded-md px-3 py-2 text-sm ${
          isActive
            ? "bg-slate-900 text-white"
            : "text-slate-300 hover:bg-slate-900 hover:text-white"
        }`
      }
    >
      {props.children}
    </NavLink>
  );
}

function SignInCta(): JSX.Element {
  const location = useLocation();
  // Preserve the current path so after auth the user lands back where they
  // started. We drop "/sign-in" itself to avoid a pointless round-trip.
  const current = location.pathname + location.search;
  const redirect = current.startsWith("/sign-in") ? "/" : current;
  return (
    <Link
      to={`/sign-in?redirect=${encodeURIComponent(redirect)}`}
      className="rounded-md bg-indigo-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-400"
    >
      Sign in
    </Link>
  );
}

function UserMenu({ user }: { user: AuthUser }): JSX.Element {
  const { entitlements, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement | null>(null);

  // Close on outside click / Escape. Kept cheap — only mounted listeners
  // while the menu is open.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent): void {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function handleSignOut(): Promise<void> {
    setOpen(false);
    await signOut();
    navigate("/", { replace: true });
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full border border-slate-800 bg-slate-900 px-1 py-1 hover:border-slate-700"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MiniAvatar user={user} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-64 rounded-lg border border-slate-800 bg-slate-950 p-2 shadow-xl"
        >
          <div className="border-b border-slate-800 px-3 py-2">
            <div className="flex items-center gap-2">
              <div className="truncate text-sm text-slate-100">
                {user.name ?? user.email}
              </div>
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] ${planBadgeClass(entitlements.plan)}`}
              >
                {planLabel(entitlements.plan)}
              </span>
            </div>
            <div className="truncate text-xs text-slate-500">{user.email}</div>
          </div>
          <Link
            to="/account"
            onClick={() => setOpen(false)}
            className="block rounded-md px-3 py-2 text-sm text-slate-200 hover:bg-slate-900"
            role="menuitem"
          >
            Account &amp; plan
          </Link>
          <button
            type="button"
            onClick={handleSignOut}
            className="block w-full rounded-md px-3 py-2 text-left text-sm text-slate-300 hover:bg-slate-900"
            role="menuitem"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function MiniAvatar({ user }: { user: AuthUser }): JSX.Element {
  if (user.avatarUrl) {
    return (
      <img
        src={user.avatarUrl}
        alt=""
        className="h-7 w-7 rounded-full object-cover"
      />
    );
  }
  const initial = (user.name ?? user.email).trim().charAt(0).toUpperCase() || "?";
  return (
    <div className="grid h-7 w-7 place-items-center rounded-full bg-slate-800 text-xs font-medium text-slate-100">
      {initial}
    </div>
  );
}
